import { StepperStatusType, DraftWaypointStatus, MapSourceType } from '@caresend/types';
import { isDraftWaypointDenormalized, formatTimeZoneDate, formatFullName, getDraftWaypointDateTimeLabels, getDayOfWeek, formatOpeningHours, maybeAdjustFridayOpeningHours, getHoursForDay } from '@caresend/utils';
import { getStore } from '../../../../../store/helpers/initStore.js';

/**
 * Functions to get the dispatch steps for a pending draft waypoint.
 *
 * Due to permission issues on the patient app, the dispatch steps are calculated
 * on the `getDenormalizedOrder` function on the backend, which is triggered
 * whenever an order is created or updated. The problem with this is that the
 * function can take 30 seconds to execute, so we dynamically generate the steps
 * here to improve the user experience.
 *
 * Dispatch steps are only displayed for in-home visits.
 */
const getLocalFirstStep = (draftWaypoint) => {
    var _a;
    const store = getStore();
    const { status } = draftWaypoint !== null && draftWaypoint !== void 0 ? draftWaypoint : {};
    switch (status) {
        case DraftWaypointStatus.CANCELED: {
            const canceledByName = formatFullName(store.state.auth.user);
            return {
                title: `Canceled by ${canceledByName}`,
                isCompleted: false,
                isFailed: true,
            };
        }
        case DraftWaypointStatus.FINALIZED: {
            const isDenormalized = draftWaypoint && isDraftWaypointDenormalized(draftWaypoint);
            const dateFormat = !(draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.drawPlaceID)
                ? 'M/D/YYYY, h:mm a'
                : 'M/D/YYYY';
            const dateTime = isDenormalized
                ? draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.date
                : formatTimeZoneDate((_a = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date, dateFormat);
            return {
                title: `Scheduled for ${dateTime}`,
                isCompleted: true,
            };
        }
        case DraftWaypointStatus.PENDING:
        default:
            return {
                title: 'Pending scheduling',
                isPending: true,
            };
    }
};
const getLocalPatientVisitStepSubtitle = (isBrickAndMortar, draftWaypoint, placeData) => {
    var _a;
    const store = getStore();
    if (!isBrickAndMortar) {
        const { timeLabel } = getDraftWaypointDateTimeLabels(draftWaypoint);
        return `Scheduled for ${timeLabel}`;
    }
    if (!draftWaypoint)
        return '';
    const date = isDraftWaypointDenormalized(draftWaypoint)
        ? draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.date
        : (_a = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date;
    if (!date)
        return '';
    const dayOfWeek = getDayOfWeek(date);
    if (!dayOfWeek || !(placeData === null || placeData === void 0 ? void 0 : placeData.openingHours))
        return '';
    const fridayCutoffTime = store.getters['variables/getFridayBrickAndMortarCutoffTime'];
    const formattedHours = formatOpeningHours(maybeAdjustFridayOpeningHours(placeData.openingHours, fridayCutoffTime));
    return getHoursForDay(dayOfWeek, formattedHours);
};
const getLocalPatientVisitStep = (draftWaypoint, placeData) => {
    const { drawPlaceID, location } = draftWaypoint !== null && draftWaypoint !== void 0 ? draftWaypoint : {};
    const isBrickAndMortar = !!drawPlaceID;
    const mapMarker = location && {
        location: {
            type: 'Point',
            coordinates: [
                location.lng,
                location.lat,
            ],
        },
        waypointType: MapSourceType.PATIENT,
    };
    return {
        title: isBrickAndMortar ? 'Walk-in visit' : 'Visit',
        subtitle: getLocalPatientVisitStepSubtitle(isBrickAndMortar, draftWaypoint, placeData),
        ...(mapMarker && { mapMarker }),
        type: StepperStatusType.PATIENT_VISIT,
        waypointLetter: 'A',
    };
};
/**
 * Dispatch steps are calculated in cloud, and stored in the dernomalized
 * order. This function is only used just after scheduling to display data
 * to the user immediately.
 *
 * Defaults to pending status if no draft waypoint passed.
 * In addition, we assume the nurse is a phlebotomist rather than fetching
 * the nurse's medical title for permission reasons.
 */
const getLocalDraftWaypointDispatchSteps = (draftWaypoint, placeData) => {
    const isBrickAndMortar = !!(draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.drawPlaceID);
    const dispatchSteps = [
        getLocalFirstStep(draftWaypoint),
        ...(isBrickAndMortar ? [] : [
            {
                title: 'Phlebotomist in transit',
            },
        ]),
        getLocalPatientVisitStep(draftWaypoint, placeData),
        ...(isBrickAndMortar ? [] : [
            {
                title: 'Phlebotomist in transit',
            },
        ]),
        {
            title: 'Drop off',
            type: StepperStatusType.DROP_OFF,
            waypointLetter: 'B',
        },
        {
            title: 'Sample at lab',
        },
    ];
    return dispatchSteps;
};

export { getLocalDraftWaypointDispatchSteps };
